import { IonCard, IonSpinner } from '@ionic/react'
import { GroupingOptionsV2 } from '@vae/server/src/utils/arrays/types/GroupingOptions'
import { ChartDataset, Chart as ChartJS, registerables } from 'chart.js'
import annotationPlugin from 'chartjs-plugin-annotation'
import dayjs from 'dayjs'
import React, { FC, useContext, useMemo, useRef, useState } from 'react'
import { Chart } from 'react-chartjs-2'
import { ChartJSOrUndefined } from 'react-chartjs-2/dist/types'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { AppContext } from '../../../../contexts/AppContext'
import useUser from '../../../../contexts/hooks/useUser'
import { UserContext } from '../../../../contexts/UserContext'
import { TooltipData } from '../../components/baseTooltip/BaseTooltip'
import getTimeString from '../../components/baseTooltip/getTimeString'
import ChartTimeSwitch from '../../components/ChartTimeSwitch'
import getLabels from '../../utils/getLabels'
import ProductionChartScroller from './components/ProductionChartScroller'
import ProductionTooltip from './components/ProductionTooltip'
import { useProductionChartTime } from './hooks/useProductionChartTime'
import useProductionData from './hooks/useProductionData'
import { getProductionChartOptions } from './options/productionChartOptions'

ChartJS.register(...registerables, annotationPlugin)

interface ProductionChartProps {
	anchorRef: React.RefObject<HTMLDivElement>
	timespan?: GroupingOptionsV2
}

const ProductionChart: FC<ProductionChartProps> = ({ timespan, anchorRef }) => {
	const { isDarkMode } = useContext(AppContext)
	const { isAnonymousUser, logOut } = useContext(UserContext)

	const { user } = useUser()
	const history = useHistory()

	const time = useProductionChartTime(timespan)
	const { data: chartData, isLoading } = useProductionData({
		from: time.startTime.tz('Europe/Helsinki').toISOString(),
		to: time.endTime.tz('Europe/Helsinki').toISOString(),
		timespan: time.span,
		isDarkMode,
	})

	const { t } = useTranslation('pages_electricity')

	const [activeElement, setActiveElement] = useState<number>(dayjs().hour())
	const barRef = useRef<ChartJSOrUndefined<'bar' | 'line', number[], string>>(null)

	const chartLabels = getLabels({
		from: time.startTime.tz('Europe/Helsinki').format(),
		grouping: time.span,
		language: user?.language || 'fi',
	})

	const handleChartClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
		if (barRef.current) {
			const index = barRef.current.scales.x.getValueForPixel(event.nativeEvent.offsetX)
			const max = chartData?.labels?.length ? chartData.labels.length - 1 : 0

			if (index && index <= 0) setActiveElement(0)
			else if (index && index > max) setActiveElement(max)
			else setActiveElement(index ?? 0)
		}
	}

	const tooltipData = useMemo((): TooltipData[] => {
		if (isLoading) return []

		const estimatedSavings = chartData?.estimatedSavings.groupedData[activeElement]?.value ?? 0
		const prodAfterSold = chartData?.prodAfterSold[activeElement] ?? 0

		const sold =
			chartData?.datasets.find(dataset => dataset.id === 'production-sold')?.data[activeElement] ??
			0
		const exchangePrice =
			chartData?.datasets.find(dataset => dataset.id === 'exchangePrices')?.data[activeElement] ?? 0

		return [
			{
				label: t('kaytetty'),
				value: Math.max(prodAfterSold, 0),
				color: isDarkMode ? '#52ae32' : '#127d27',
			},
			{
				label: t('myynti'),
				value: sold,
				color: isDarkMode ? '#acd7d4' : '#40bfa9',
			},
			{
				label: time.span === 'day' ? t('hinta') : t('averagePrice'),
				value: exchangePrice,
				color: '#ffb30d',
				overrideUnit: t('centsPerKWh'),
			},
			{
				label: t('saasto'),
				value: estimatedSavings > 100 ? estimatedSavings / 100 : Math.max(estimatedSavings, 0),
				color: '#c3c3c3',
				overrideUnit: estimatedSavings > 100 ? '€' : 'snt',
			},
		]
	}, [activeElement, chartData, user?.language, time.span])

	const options = useMemo(() => {
		const labels = chartLabels.map((label, index) => {
			const localTime = chartData?.exchangePrices.groupedData[index]?.localTime
			if (!localTime) return label
			return getTimeString(
				localTime,
				label,
				time.span === 'hour' ? 'day' : time.span,
				user?.language || 'fi'
			)
		})

		return getProductionChartOptions({
			activeElement,
			xLabels: labels,
			isDarkMode,
			onHover: setActiveElement,
		})
	}, [activeElement, chartData])

	const onRegister = () => {
		logOut()
		history.push('/register')
	}

	return (
		<div className='m-auto flex flex-col items-center justify-center gap-md w-full'>
			<ProductionChartScroller
				time={time}
				isLoading={!isAnonymousUser && isLoading}
				chartData={chartData}
			/>

			<IonCard
				id='chart'
				className='w-full 2xl:w-[60%] rounded-b-[16px] bg-white dark:bg-brand-secondary-200-dark shadow-card'
			>
				{isAnonymousUser ? (
					<div
						onClick={onRegister}
						className='flex p-4 items-center justify-center text-center cursor-pointer hover:bg-gray-300'
					>
						{t('authenticateProduction')}
					</div>
				) : (
					<ProductionTooltip
						activeElement={activeElement}
						isLoading={isLoading}
						timespan={time.span}
						tooltipData={tooltipData}
						labels={chartLabels ?? []}
						chartData={chartData}
						setActiveElement={setActiveElement}
					/>
				)}

				<div
					ref={anchorRef}
					className={`canvas-container border bg-white border-gray-300 dark:border-brand-secondary-200-dark dark:bg-[#063a3e] ${
						isLoading ? 'loading' : ''
					}`}
				>
					{!isAnonymousUser && isLoading ? (
						<IonSpinner />
					) : (
						<Chart
							ref={barRef}
							type='bar'
							options={options}
							data={{
								labels: chartLabels ?? [],
								datasets: (chartData?.datasets as ChartDataset<'bar' | 'line'>[]) ?? [],
							}}
							onClick={handleChartClick}
						/>
					)}
				</div>

				<ChartTimeSwitch time={time} />
			</IonCard>
		</div>
	)
}

export default ProductionChart
